import { Directive } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import { Locale } from '@app/types/translatable.type';
import { TranslateService } from '@ngx-translate/core';

/**
 * TimeFormatValidator
 *
 * This directive provides a custom validator for time input formats.
 * It validates that the input matches either a 12-hour time format (e.g., "12:00" or "12:00 AM/PM").
 *
 * Usage: Add the 'timeFormatValidator' attribute to an input element to apply this validator.
 */
@Directive({
    selector: '[timeFormatValidator]',
    providers: [{ provide: NG_VALIDATORS, useExisting: TimeFormatValidator, multi: true }],
})
export class TimeFormatValidator implements Validator {
    private currentLocale: Locale;

    private REGEX: Record<Locale, RegExp> = {
        en_CA: /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?[APap][Mm]$/, // Regex to match time in 12-hour format, with or without AM/PM
        fr_CA: /^(0?[0-9]|(1[0-9]|2[0-3])):[0-5][0-9]$/, // Regex to match time in 24-hour format without AM/PM
    };

    constructor(translateService: TranslateService) {
        this.currentLocale = (translateService.currentLang as Locale) ?? 'en_CA';
    }

    /**
     * Validates the input control's value against the time format regex.
     *
     * @param control The form control to validate
     * @returns ValidationErrors if invalid, null if valid
     */
    validate(control: AbstractControl): ValidationErrors | null {
        // Check if the control has a value
        if (!control.value) {
            return { required: true };
        }

        // Test the value against the regex
        const valid = this.REGEX[this.currentLocale].test(control.value);

        // Return null if valid, or an error object if invalid
        return valid ? null : { invalidTimeFormat: true };
    }
}
